import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const NavigationSelector = ({
  data = [],
  onSelectionChange,
  initialIndex = 0,
  labelKey = 'label', // Key to use for display text
  valueKey = 'value', // Key to use for value
  placeholder = 'No items',
  className = '',
  disabled = false,
  // Background color props
  containerBg = 'bg-white', // Container background
  buttonBg = 'bg-gray-100', // Toggle buttons background
  buttonHoverBg = 'hover:bg-gray-200', // Toggle buttons hover background
  displayBg = 'bg-white', // Display area background
  displayBorder = 'border-gray-200' // Display area border color
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showDropdown, setShowDropdown] = useState(false);

  // Update current index when data changes or initialIndex changes
  useEffect(() => {
    if (data.length > 0) {
      const validIndex = Math.min(initialIndex, data.length - 1);
      setCurrentIndex(validIndex);
    }
  }, [data, initialIndex]);

  // Call onSelectionChange when selection changes
  useEffect(() => {
    if (data.length > 0 && currentIndex >= 0 && currentIndex < data.length) {
      onSelectionChange?.(data[currentIndex], currentIndex);
    }
  }, [currentIndex, data, onSelectionChange]);

  const handlePrevious = () => {
    if (!data || data.length === 0 || disabled) return;

    const newIndex = currentIndex === 0 ? data.length - 1 : currentIndex - 1;

    setCurrentIndex(newIndex);
  };

  const handleNext = () => {
    if (!data || data.length === 0 || disabled) return;

    const newIndex = currentIndex === data.length - 1 ? 0 : currentIndex + 1;

    setCurrentIndex(newIndex);
  };

  const handleItemSelect = (item, index) => {
    setCurrentIndex(index);
    setShowDropdown(false);
  };

  const getCurrentItem = () => {
    if (!data || data.length === 0 || currentIndex >= data.length) {
      return { [labelKey]: placeholder, [valueKey]: null };
    }
    return data[currentIndex];
  };

  const getDisplayValue = (item) => {
    return item[labelKey] || placeholder;
  };

  const getItemValue = (item) => {
    return item[valueKey];
  };

  return (
    <div className={`relative ${className}`}>
      <div className={`flex items-center gap-2 ${containerBg} p-1 rounded-lg`}>
        <button
          type="button"
          onClick={handlePrevious}
          disabled={!data || data.length <= 1 || disabled}
          className={`flex items-center justify-center w-8 h-9 ${buttonBg} ${buttonHoverBg} disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-md`}>
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>

        <button
          type="button"
          onClick={() => !disabled && setShowDropdown(!showDropdown)}
          disabled={disabled}
          className={`flex items-center gap-2 px-4 py-[0.3rem] ${displayBg} border ${displayBorder} rounded-md shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}>
          <span className="font-medium text-gray-900 text-sm whitespace-nowrap">
            {getDisplayValue(getCurrentItem())}
          </span>
        </button>

        <button
          type="button"
          onClick={handleNext}
          disabled={!data || data.length <= 1 || disabled}
          className={`flex items-center justify-center w-8 h-9 ${buttonBg} ${buttonHoverBg} disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-md`}>
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Dropdown Menu */}
      {showDropdown && data && data.length > 0 && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-[9999] max-h-60 overflow-y-auto">
          {data.map((item, index) => (
            <button
              key={getItemValue(item) || index}
              type="button"
              onClick={() => handleItemSelect(item, index)}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                index === currentIndex ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
              }`}>
              {getDisplayValue(item)}
            </button>
          ))}
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDropdown && <div className="fixed inset-0 z-0" onClick={() => setShowDropdown(false)} />}
    </div>
  );
};

export default NavigationSelector;
